import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";

export default class extends Controller {
  static targets = [
    "emptyScreen",
    "entries",
    "summary",
    "summaryButtons",
    "needsAttentionBadge",
    "nextButton",
    "rowsCount",
    "completeCount",
    "needAttentionCount",
  ];

  static values = {
    createEntryUrl: String,
    needsAttentionString: String,
  };

  // Check if there's at least one complete payroll entry
  get hasCompleteEntries() {
    return (
      this.entriesTarget.querySelectorAll(
        "[data-payroll-entry]:not([data-payroll-entry-incomplete])"
      ).length > 0
    );
  }

  connect() {
    this._toggleBetweenEmptyAndEntriesState();
    this._toggleSecondarySummaryVisibility();
    this._updateSummaryCounts();
  }

  get hasEntries() {
    return (
      this.hasEntriesTarget &&
      this.entriesTarget.querySelector("[data-payroll-entry]") !== null
    );
  }

  get entriesCount() {
    return this.entriesTarget.querySelectorAll("[data-payroll-entry]").length;
  }

  get completeEntriesCount() {
    return this.entriesCount - this.incompleteEntriesCount;
  }

  get incompleteEntriesCount() {
    return this.entriesTarget.querySelectorAll(
      "[data-payroll-entry-incomplete]"
    ).length;
  }

  createEmptyEntry() {
    httpRequest(this.createEntryUrlValue, { method: "POST" })
      .then((response) => response.text())
      .then((html) => this.entriesTarget.insertAdjacentHTML("beforeend", html))
      .then(() =>
        this.entriesTarget
          .querySelector("[data-payroll-entry]:last-child")
          .scrollIntoView({ behavior: "smooth" })
      )
      .then(() => this.onEntryCreated());
  }

  onEntryCreated() {
    setTimeout(() => this._updateStates());
  }

  onEntryUpdated() {
    setTimeout(() => this._updateStates());
  }

  onEntryDeleted() {
    setTimeout(() => this._updateStates());
  }

  onExcelUploadRequested() {
    this.dispatch("excelUploadRequested");
  }

  _updateStates() {
    this._updateNeedsAttentionBadge();
    this._toggleBetweenEmptyAndEntriesState();
    this._updateNextButtonState();
    this._updateSummaryCounts();
    this._updateStepNavigationState();
    this._toggleSecondarySummaryVisibility();
  }

  // Disable/enable preview and addons step navigation based on complete entries
  _updateStepNavigationState() {
    const hasComplete = this.hasCompleteEntries;

    // Find all step indicator links for steps 4 (preview) and 5 (addons)
    // URL pattern is /:locale/wage-calculations/wizard/:id/:step
    // So we look for hrefs ending with /preview or /addons
    document
      .querySelectorAll('a[href$="/preview"], a[href$="/addons"]')
      .forEach((link) => {
        if (hasComplete) {
          // Keep the link active
          link.classList.remove("pointer-events-none", "opacity-50");
        } else {
          // Disable the link visually
          link.classList.add("pointer-events-none", "opacity-50");
        }
      });
  }

  _updateNextButtonState() {
    if (this.hasEntries && this.incompleteEntriesCount === 0) {
      this.nextButtonTarget.removeAttribute("disabled");
    } else {
      this.nextButtonTarget.setAttribute("disabled", "true");
    }
  }

  _updateNeedsAttentionBadge() {
    const incompleteCount = this.incompleteEntriesCount;

    if (incompleteCount > 0) {
      const message = this.needsAttentionStringValue.replace(
        "__COUNT__",
        incompleteCount
      );
      this.needsAttentionBadgeTarget.textContent = message;
      this.needsAttentionBadgeTarget.classList.remove("hidden");
    } else {
      this.needsAttentionBadgeTarget.classList.add("hidden");
    }
  }

  _updateSummaryCounts() {
    this.rowsCountTargets.forEach(
      (target) => (target.textContent = this.entriesCount)
    );
    this.completeCountTargets.forEach(
      (target) => (target.textContent = this.completeEntriesCount)
    );
    this.needAttentionCountTargets.forEach(
      (target) => (target.textContent = this.incompleteEntriesCount)
    );
  }

  _toggleSecondarySummaryVisibility() {
    if (!this.hasSummaryTarget || this.summaryTargets.length < 2) return;

    if (this.entriesCount > 0) {
      this.summaryTargets[1].classList.remove("hidden");
    } else {
      this.summaryTargets[1].classList.add("hidden");
    }
  }

  _toggleBetweenEmptyAndEntriesState() {
    if (!this.hasEmptyScreenTarget || !this.hasSummaryButtonsTarget) return;

    if (this.hasEntries) {
      this.emptyScreenTarget.classList.add("hidden");
      this.summaryButtonsTargets.forEach((target) =>
        target.classList.remove("hidden")
      );
    } else {
      this.emptyScreenTarget.classList.remove("hidden");
      this.summaryButtonsTargets.forEach((target) =>
        target.classList.add("hidden")
      );
    }
  }
}
